import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { serializeUrl } from 'app/core/shared/helper/url.helper';
import { OptionTypeService } from 'app/modules/option-type/services/option-type.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class EquipRegistrationServices {
  private _equipmentRegistration: BehaviorSubject<any> = new BehaviorSubject(null);
  private _equipmentHistoricalData: BehaviorSubject<any> = new BehaviorSubject(null);
  private _isReplace: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _isDisabled: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _viewEquipment: BehaviorSubject<any> = new BehaviorSubject(null);

  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private authService: AuthService,
    private metadataServices: OptionTypeService) {
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  set viewEquipment(equipment: any) {
    this._viewEquipment.next(equipment);
  }

  /**
   * Getter for data
   */
  get equipmentRegistration$(): Observable<any> {
    return this._equipmentRegistration.asObservable();
  }

  get equipmentHistoricalData$(): Observable<any> {
    return this._equipmentHistoricalData.asObservable();
  }

  get isReplace$(): Observable<boolean> {
    return this._isReplace.asObservable();
  }

  get isDisabled$(): Observable<boolean> {
    return this._isDisabled.asObservable();
  }

  get viewEquipment$(): Observable<any> {
    return this._viewEquipment.asObservable();
  }


  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get data
   */
  getEquipmentRegistration(wellStringId: any): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/GetEquipmentRegister/${wellStringId}`).pipe(
      tap((response: any) => {
        this._equipmentRegistration.next(response.data);
      }),
    );
  }

  getStrings(params) {
    return this._httpClient.get<any>(`${environment.apiURL
      }/v1/WellHeaderInformation/Strings/GetWellStrings?${serializeUrl(
        params
      )}`).pipe(
        tap((response: any) => {
          return response
        }),
      );
  }

  getEquipmentRegistrationHistoricalData(valveType: string, wellStringId: string): Observable<any> {
    let Params = new HttpParams()
    Params = Params.append('valveType', valveType);
    Params = Params.append('wellStringId', wellStringId);

    return this._httpClient.get<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/GetEquipmentRegisterHistoricalData`,
      { params: Params }
    ).pipe(
      tap((response: any) => {
        this._equipmentHistoricalData.next(response.data);
      }),
    );
  }

  /**
   * XMAS Tree
   */
  getSceXmasTreeByStringId(wellStringId: string) {
    let Params = new HttpParams()
    Params = Params.append('stringId', wellStringId);

    return this._httpClient.get<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/GetSceXmasTrees`, {
      params: Params
    }).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  addSceXmasTree(payload) {
    payload.id = null

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/AddSceXmasTree`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateSceXmasTree(payload) {
    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateSceXmasTree`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  /**
   * SCE Valve
   */
  addSceValve(payload) {
    payload.id = null
    payload.isActuated = payload.actuatorType == 'Pneumatic' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/AddSceValve`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateSceValve(payload) {
    payload.isActuated = payload.actuatorType == 'Pneumatic' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateSceValve`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  /**
   * SCE Wellhead
   */
  addSceWellhead(payload) {
    payload.id = null
    payload.isActuated = payload.actuatorType == 'Pneumatic' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/AddSceWellHead`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateSceWellhead(payload) {
    payload.isActuated = payload.actuatorType == 'Pneumatic' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateSceWellHead`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  /**
   * SCE Scssv/ TRSV
   */

  addTrSssv(payload) {
    payload.id = null
    payload.blowingGas = null
    payload.slamTest = null

    payload.isLockedOpen = payload.isLockedOpen == 'true' ? true : false
    payload.isSelfEqualize = payload.isSelfEqualize == 'true' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/AddTrSssv`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateSceScsssv(payload) {
    payload.blowingGas = null
    payload.slamTest = null

    payload.isLockedOpen = payload.isLockedOpen == 'true' ? true : false
    payload.isSelfEqualize = payload.isSelfEqualize == 'true' ? true : false

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateSceScssv`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  /**
  * WRSV
  */
  addWrsv(payload) {
    payload.id = null

    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/AddSceWrsv`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateWrsv(payload) {
    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateSceWrsv`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }


  /**
  * get Sce Valve Type Id
  */
  getSceValveTypeId(valveType: string) {
    return this.metadataServices.getOptionItems("md_sce_valve_type").pipe(
      map((res) => {
        const sceValveList = res.data;
        const matchingValve = sceValveList.find((x) => x.alternate === valveType);
        return matchingValve ? matchingValve.id : null;
      })
    );
  }

  /**
  * get Sce Valve Type Id
  */
  getSssvTypeId(sssvType: string) {
    return this.metadataServices.getOptionItems("md_sssv_type").pipe(
      map((res) => {
        const scssvList = res.data;
        const matchingSssv = scssvList.find((x) => x.alternate === sssvType);
        return matchingSssv ? matchingSssv.id : null;
      })
    );
  }

  getSssvType() {
    return this.metadataServices.getOptionItems("md_sssv_type").pipe(
      map((res) => {
        return res.data
      })
    );
  }

  // for trsssv/wrsv 
  setReplace(isReplace: boolean) {
    this._isReplace.next(isReplace);
  }

  setDisabledToggle(isDisabled: boolean) {
    this._isDisabled.next(isDisabled);
  }

  downloadEquipmentRegHistoricalRecords(valveType: string, wellStringId: string) {
    let params = new HttpParams()
      .set('valveType', valveType)
      .set('wellStringId', wellStringId)


    return this._httpClient.get(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/DownloadEquipmentRegisterHistoricalData`, {
      params: params,
      responseType: 'blob',
    }).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  deleteEquipmentHistory(valveType: string, wellStringId: string, equipmentId: string): Observable<any> {
    let params = new HttpParams()
      .set('valveType', valveType)
      .set('wellStringId', wellStringId)
      .set('equipmentId', equipmentId)


    return this._httpClient.delete(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/DeleteValveData/DeleteValveData`, {
      params: params,
    }).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateHistroicalData(payload) {
    return this._httpClient.post<any>(`${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/UpdateHistoricalSceValve`, payload).pipe(
      tap((response: any) => {
        return response
      }),
    );
  }

  updateHistoricalData(sceType: 'Crown Valve' | 'XmasTree' | 'WellHead' | 'Scssv' | 'Wrsv', payload: any) {
    const endpointMap: Record<string, string> = {
      'Crown Valve': 'UpdateHistoricalSceValve',
      'Lower Master Valve': 'UpdateHistoricalSceValve',
      'King Wing Valve': 'UpdateHistoricalSceValve',
      'Surface Safety Valve': 'UpdateHistoricalSceValve',
      'Wing Valve': 'UpdateHistoricalSceValve',
      'Xmas Tree': 'UpdateHistoricalSceXmasTree',
      'Annulus Valve A': 'UpdateHistoricalSceWellHead',
      'Annulus Valve B': 'UpdateHistoricalSceWellHead',
      'Annulus Valve C': 'UpdateHistoricalSceWellHead',
      'Annulus Valve D': 'UpdateHistoricalSceWellHead',
      'TR-SSSV': 'UpdateHistoricalSceScssv',
      'WRSV': 'UpdateHistoricalSceWrsv',
    };

    const endpoint = endpointMap[sceType];
    if (!endpoint) {
      throw new Error(`Unknown sceType: ${sceType}`);
    }

    return this._httpClient.post<any>(
      `${environment.apiURL}/${environment.apiVersion}/EquipmentRegister/EquipmentRegister/${endpoint}`,
      payload
    );
  }
}
